import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import DataService from "features/Application/services/dataServices/typedDataService";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { type DistributedOpUpdateParams } from "interfaces";
import { formatFilters } from "utils/filterMapUtils";
import { escapeTerm } from "utils/searchUtils";
import { pluralize } from "utils/stringUtils";
import { embeddedVideosFilterTypes } from "../../types/models";
import { fetchBegin, fetchSuccess } from "../EmbeddedVideosOverviewSlice";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const fetchEmbeddedVideos =
  (skip: number, top: number, sortBy?: string, sortOrder?: string, append?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBegin());
      const filtersState = getState().library.embeddedVideos.filters;
      const searchTerm = escapeTerm(filtersState.search);
      const filterQueryParams = formatFilters(filtersState.appliedFilter, embeddedVideosFilterTypes);
      const result = await DataService.content.getContent({
        top,
        skip,
        sortBy,
        sortOrder,
        filters: filterQueryParams,
        term: searchTerm,
        showPurchased: true,
        contentType: "embeddedvideo",
      });

      const items = result.data.map((item) => ({ ...item, thumbnailUrl: item.bag?.thumbnailUrl }));
      const totalCount = Number.parseInt(result.headers[countHeaderName]);
      dispatch(
        fetchSuccess({
          items,
          totalCount,
          append,
        }),
      );
    } catch {
      dispatch(
        fetchSuccess({
          items: [],
          totalCount: 0,
        }),
      );
    }
  };

export const deleteEmbeddedVideos =
  (ids: number[], refetchEmbeddedVideos: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.embeddedVideos.deleteEmbeddedVideos(ids);
      return data;
    };

    const idsCount = ids.length;
    const entity = pluralize("Embedded Video", idsCount);
    const updateParams: DistributedOpUpdateParams = {
      id: "DeleteEmbeddedVideos",
      title: `Deletion of ${entity}`,
      onCompleted: () => refetchEmbeddedVideos(),
      getOperationProps,
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been deleted!`,
      failureTransientMessage: `${entity} delete failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const changeEmbeddedVideosVisibility =
  (ids: number[], hide: boolean, onCompleted: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const idsCount = ids.length;
    const entity = pluralize("Embedded Video", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "VisibilityEmbeddedVideoDistributedOperation",
      title: `${hide ? "Showing" : "Hiding"} ${entity}`,
      onCompleted,
      getOperationProps: async () => {
        const { data } = await DataService.embeddedVideos.changeEmbeddedVideosVisibility(ids, hide);
        return data;
      },
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been ${hide ? "shown" : "hidden"} successfully!`,
      failureTransientMessage: `${entity} ${hide ? "showing" : "hiding"} failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const duplicateEmbeddedVideos =
  (ids: number[], fetchEmbeddedVideos: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.embeddedVideos.duplicateEmbeddedVideos(ids);
      return data;
    };

    const idsCount = ids.length;
    const bannerPrefix = pluralize("Embedded Video", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DuplicateEmbeddedVideos",
      title: `Duplication of ${bannerPrefix}`,
      onCompleted: () => fetchEmbeddedVideos(),
      getOperationProps,
      successTransientMessage: `${bannerPrefix} ${idsCount === 1 ? "has" : "have"} been duplicated!`,
      failureTransientMessage: `${bannerPrefix} duplicate failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
